/**
 * E-invoice address help for the billing-details forms (member portal + staff tenant card — the two apps
 * don't share a lib, so this file exists in both; keep them identical).
 *
 * Mirrors the backend rules (crm/services/einvoice.py) and what the accounting system can hold
 * (billing/providers/fortnox/provider.py `_delivery_fields`): a GLN — bare 13 digits or 0088:<GLN> — is
 * stored on the customer as its GLN; 0007:<own organisation number> needs nothing extra (the organisation
 * number identifies it in Peppol); any other scheme is accepted and set up by hand in the accounting
 * system. The backend has the final word — its 400 message is shown under the field.
 */

export type EinvoiceKind = 'gln' | 'peppol-0007' | 'peppol-other' | 'unknown';

export interface EinvoiceHelp {
  kind: EinvoiceKind;
  hint: string;
  /** Something the backend will refuse; shown before saving. */
  problem: string | null;
}

const digits = (v: string | null | undefined) => (v ?? '').replace(/\D/g, '');

/** Swedish org number digits; the 12-digit 16XXXXXXXXXX form folds to 10 (as the backend does). */
export function orgnrDigits(v: string | null | undefined): string {
  const d = digits(v);
  return d.length === 12 && d.startsWith('16') ? d.slice(2) : d;
}

/** 13 digits with a correct GS1 mod-10 check digit (weights 1,3,1,3… from the left). */
export function validGln(value: string): boolean {
  if (!/^\d{13}$/.test(value)) return false;
  const total = [...value.slice(0, 12)].reduce((sum, ch, i) => sum + Number(ch) * (i % 2 ? 3 : 1), 0);
  return (10 - (total % 10)) % 10 === Number(value[12]);
}

export function einvoiceHelp(address: string, organisationNumber?: string | null): EinvoiceHelp {
  const a = address.replace(/\s/g, '');
  if (!a) {
    return { kind: 'unknown', hint: 'A GLN (13 digits) or a PEPPOL id such as 0007:5560360793.', problem: null };
  }
  const gln = /^\d+$/.test(a) ? a : /^0088:(\d+)$/.exec(a)?.[1];
  if (gln !== undefined) {
    return {
      kind: 'gln',
      hint: 'GLN — e-invoices are sent to this address from the accounting system.',
      problem: validGln(gln) ? null : "That GLN isn't valid — it must be 13 digits with a correct check digit.",
    };
  }
  const m = /^(\d{4}):(\S+)$/.exec(a);
  if (m && m[1] === '0007') {
    const id = orgnrDigits(m[2]);
    const own = orgnrDigits(organisationNumber);
    let problem: string | null = null;
    if (id.length !== 10) problem = 'A 0007 PEPPOL id must be a 10-digit Swedish organisation number.';
    else if (own && id !== own) problem = 'The 0007 PEPPOL id must be your own organisation number.';
    return { kind: 'peppol-0007', hint: 'PEPPOL id from your organisation number — e-invoices reach you through Peppol.', problem };
  }
  if (m) {
    return {
      kind: 'peppol-other',
      hint: 'Accepted. E-invoicing to this id is set up by hand in the accounting system.',
      problem: null,
    };
  }
  return {
    kind: 'unknown',
    hint: 'A GLN (13 digits) or a PEPPOL id such as 0007:5560360793.',
    problem: 'Use a GLN (13 digits) or a PEPPOL id written as scheme:identifier, e.g. 0007:5560360793.',
  };
}

/** Soft Peppol Directory confirmation of a saved PEPPOL id. Advisory: it never blocks saving. */
export function peppolStatusText(registered: boolean | null | undefined): { color: 'green' | 'gray'; text: string } | null {
  if (registered === true) return { color: 'green', text: 'Registered in Peppol — can receive invoices.' };
  if (registered === false) {
    return { color: 'gray', text: 'Not found in the Peppol Directory. Listing there is optional, so this may still be right.' };
  }
  return null;
}

/** Which form field a billing-details 400 belongs to. The backend prefixes field errors ("postcode: …");
 *  a model-level error (`__all__: …`) belongs to no field (`form`); the one unprefixed message is the
 *  e-invoice address check. */
export function billingErrorField(detail: string): { field: string; message: string } {
  const m = /^([a-z_0-9]+): (.*)$/s.exec(detail);
  if (m) return { field: m[1] === '__all__' ? 'form' : m[1], message: m[2] };
  return { field: 'einvoice_address', message: detail };
}
